import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  getCustomerLimits,
  getCustomerLimit,
  resetCustomerLimit,
  updateCustomerLimits,
  type CustomerLimitsPayload,
} from "src/services/customerLimitsService";
import { QueryNames } from "./queries.constant";
import { useGeneralRefetchOverride } from "./general.queries";

export const useQueryGetCustomerLimits = (
  clientId?: string | number,
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = Boolean(clientId) && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      ...options,
      queryKey: [...QueryNames.customerLimits, clientId, language],
      queryFn: () => getCustomerLimits(clientId as string | number, language),
      refetchOnMount: true,
      retry: false,
      enabled,
    }),
    enabled
  );
};

export const useQueryGetCustomerLimit = (
  clientId?: string | number,
  code?: string,
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = Boolean(clientId && code) && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      ...options,
      queryKey: [...QueryNames.customerLimits, clientId, code, language],
      queryFn: () => getCustomerLimit(clientId as string | number, code as string, language),
      refetchOnMount: "always",
      staleTime: 0,
      retry: false,
      enabled,
    }),
    enabled
  );
};

export const useMutationUpdateCustomerLimits = (clientId?: string | number) => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: CustomerLimitsPayload) =>
      updateCustomerLimits(clientId as string | number, payload, language),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: [...QueryNames.customerLimits, clientId] });
    },
  });
};

export const useMutationResetCustomerLimit = (clientId?: string | number) => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (code: string) => resetCustomerLimit(clientId as string | number, code, language),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: [...QueryNames.customerLimits, clientId] });
    },
  });
};
